import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMarketData } from '../../hooks/useMarketData';
import { useVendors } from '../../hooks/useVendors';
import { useVendorSelection } from '../../hooks/useVendorSelection';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useSheetHeight } from '../../hooks/useSheetHeight';
import { useDeepLinkFocus } from '../../hooks/useDeepLinkFocus';
import { useRouteState } from '../../router';
import type { Vendor } from '../../types';
import { centroidFocusTarget, type FocusTarget } from '../Map/focus';
import { MapView } from '../Map/MapView';
import { IndoorSeasonNotice } from '../Map/IndoorSeasonNotice';
import { ListView } from '../List/ListView';
import { InfoView } from '../Info/InfoView';
import { SearchBar } from '../Filters/SearchBar';
import { FilterChips } from '../Filters/FilterChips';
import { HoursChip, TopBar } from './TopBar';
import { AppMark } from './AppMark';
import { BottomTabs, type TabId } from './BottomTabs';
import { SegmentedNav, type Segment } from './SegmentedNav';
import { ListIcon, InfoIcon, SunIcon, MoonIcon } from '../primitives/Icon';
import { useTheme } from '../../hooks/useTheme';
import { useEditorModeContext } from '../../context/EditorModeContext.utils';
import { VendorDetail } from '../../features/vendor/components/VendorDetail';
import {
  VendorSnapshot,
  VENDOR_SNAPSHOT_BLURB_ID,
} from '../../features/vendor/components/VendorSnapshot';
import { BottomSheet } from '../../features/vendor/components/BottomSheet';
import { ErrorBanner } from '../States/ErrorBanner';
import { VendorNotFoundBanner } from '../States/VendorNotFoundBanner';
import { captureEvent } from '../../observability';
import styles from './AppShell.module.css';
import topBarStyles from './TopBar.module.css';

// MapView + Leaflet are imported eagerly, not lazily: the map is the default
// landing view (route '/') and its image is the LCP element. Lazy-loading it
// added a sequential chunk-discovery hop (entry must execute before the map
// chunk is even requested), pushing LCP ~3s out on throttled mobile. A static
// import makes Leaflet a first-wave modulepreload instead.
const LazyPositionEditor = lazy(() =>
  import('../Editor/PositionEditor').then((m) => ({ default: m.PositionEditor })),
);

interface Props {
  children?: ReactNode;
}

function ThemeToggle() {
  const { effectiveTheme, toggle } = useTheme();
  const nextLabel = effectiveTheme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      className={topBarStyles.themeToggle}
      onClick={toggle}
      aria-label={`Switch to ${nextLabel} theme`}
      title={`Switch to ${nextLabel} theme`}
    >
      {effectiveTheme === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
    </button>
  );
}

type DesktopSegmentId = 'vendors' | 'info';

const DESKTOP_SEGMENTS: Segment<DesktopSegmentId>[] = [
  { id: 'vendors', label: 'Vendors', Icon: ListIcon },
  { id: 'info', label: 'Info', Icon: InfoIcon },
];

/**
 * Discriminated union for what's currently on screen (mobile flow).
 * Lets call sites narrow without juggling two booleans + a tab id.
 */
export type ViewState = { kind: 'tab'; tab: TabId } | { kind: 'vendor-detail'; vendor: Vendor };

export function AppShell({ children }: Props) {
  const { vendors, categories, nameFor, filteredVendors, filters } = useMarketData();
  const { loading, revalidating, error, errorKind, refresh, dismissError } = useVendors();
  const isDesktop = useIsDesktop();

  const { tab, vendorUid, setTab, setVendorUid } = useRouteState();
  const { isEditing } = useEditorModeContext();
  const {
    vendor: selectedVendor,
    notFound: vendorNotFound,
    open: openVendorRaw,
    close: closeVendor,
  } = useVendorSelection({
    vendors,
    vendorUid,
    setVendorUid,
  });

  /**
   * Mobile-only: whether to show the FULL detail screen on top of the map.
   * On desktop, the right-rail panel handles snapshot/detail; on mobile,
   * tapping a list row goes straight into a full-screen detail.
   */
  const [showFullDetail, setShowFullDetail] = useState(false);

  // Imperative pan/zoom target. Bumped via setFocusTarget — every distinct
  // call panes the map even if the same vendor is re-focused.
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);

  // Mobile: uid of the list row that opened the full-screen detail, so focus
  // can return to it when the detail closes. The row unmounts while the
  // detail is up (the list tab stops rendering), so the detail's own
  // useFocusTrap only ever captured <body> — AppShell owns restore instead.
  const restoreRowFocusRef = useRef<string | null>(null);
  // The mobile `<main>` region; used as the restore fallback when the
  // originating row is gone (e.g. filtered out while the detail was open).
  const mainRef = useRef<HTMLElement>(null);

  // Live measurement of the mobile bottom sheet so MapView can extend
  // maxBounds southward (bottom markers were unreachable when the sheet
  // was open) and offset focus pans above the sheet.
  const { sheetHeightPx, onSheetMount: handleSheetMount } = useSheetHeight();

  useDeepLinkFocus(vendorUid, vendors, setFocusTarget);

  /**
   * Dead deep link feedback: `/listing/<uid>` where the uid resolves to
   * nothing once vendors have loaded (unknown uid, or a vendor dropped
   * this week via `here=no`). Latched in `handleSelectionLost` below —
   * without the latch the banner would vanish the instant `closeVendor()`
   * resets `vendorUid`. Hidden again on dismiss or a successful open.
   */
  const [showVendorNotFound, setShowVendorNotFound] = useState(false);
  const dismissVendorNotFound = useCallback(() => setShowVendorNotFound(false), []);

  // Any successful open supersedes the dead-link notice — every open path
  // (map tap, list row, detail links) funnels through this wrapper.
  const openVendor = useCallback(
    (uid: string) => {
      setShowVendorNotFound(false);
      openVendorRaw(uid);
    },
    [openVendorRaw],
  );

  // Router-driven opens (browser back/forward popstate) bypass the openVendor
  // wrapper, so any selection that resolves also supersedes the notice.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot latch reset; guarded so it can't cascade
    if (selectedVendor) setShowVendorNotFound(false);
  }, [selectedVendor]);

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  // Source of the most recent open, set by each handler before it calls
  // openVendor. Defaults to 'deeplink' so path-based selection on load — and any
  // later selection not driven by a tap handler (e.g. browser back/forward to a
  // /listing/<uid> URL) — is attributed to 'deeplink' rather than a stale source.
  const vendorOpenSourceRef = useRef<'map' | 'list' | 'deeplink'>('deeplink');
  const lastOpenedUidRef = useRef<string | null>(null);

  // Single capture point for vendor_opened — fires whenever selection lands on
  // a new uid, regardless of which surface triggered it.
  useEffect(() => {
    if (!selectedVendor) {
      lastOpenedUidRef.current = null;
      vendorOpenSourceRef.current = 'deeplink';
      return;
    }
    if (selectedVendor.uid === lastOpenedUidRef.current) return;
    lastOpenedUidRef.current = selectedVendor.uid;
    captureEvent('vendor_opened', {
      vendor_uid: selectedVendor.uid,
      vendor_name: selectedVendor.name,
      category: categoryById.get(selectedVendor.categoryId)?.name ?? selectedVendor.categoryId,
      vendor_type: selectedVendor.type,
      source: vendorOpenSourceRef.current,
    });
    // Reset to the default so the next open isn't mis-attributed to this source
    // if it wasn't preceded by a tap handler setting the ref.
    vendorOpenSourceRef.current = 'deeplink';
  }, [selectedVendor, categoryById]);

  // search_performed — fires on each distinct committed (debounced) query.
  // Metadata only; the raw query text is intentionally not captured.
  const lastSearchRef = useRef('');
  useEffect(() => {
    const q = filters.search.trim();
    if (q === lastSearchRef.current) return;
    lastSearchRef.current = q;
    if (!q) return;
    captureEvent('search_performed', {
      query_length: q.length,
      has_results: filteredVendors.length > 0,
    });
  }, [filters.search, filteredVendors]);

  // Selection can be cleared out from under the detail screen (browser/
  // hardware Back popping /listing/<uid>, or onSelectionLost when a filter
  // drops the active vendor). Sync showFullDetail down so the stale flag
  // doesn't strand the next open, and so the restore effect below fires.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-way router-to-local sync
    if (!selectedVendor && showFullDetail) setShowFullDetail(false);
  }, [selectedVendor, showFullDetail]);

  // Mobile: after the full-screen detail closes back to the list, return
  // focus to the row that opened it. Opening the detail unmounted that row,
  // so the detail's useFocusTrap saw <body> as the previously-focused element
  // and restoring there would strand the user. We re-focus the freshly
  // remounted row for the still-selected vendor, falling back to the list
  // container if the row is gone (e.g. filtered out while the detail was up).
  useEffect(() => {
    if (isDesktop) return;
    if (showFullDetail) return; // detail still open — nothing to restore yet
    const uid = restoreRowFocusRef.current;
    if (!uid) return;
    restoreRowFocusRef.current = null;
    // The user navigated somewhere other than the list (e.g. "Show on Map"),
    // so there's no row to return to — leave focus where it landed.
    if (tab !== 'list') return;
    const container = mainRef.current;
    const row = container?.querySelector<HTMLElement>(
      `[data-row-uid="${CSS.escape(uid)}"]`,
    );
    const target = row ?? container;
    if (!target) return;
    if (target === container && !container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
  }, [showFullDetail, tab, isDesktop]);

  // All fanned-out position records for vendor.uid. The fallback ensures a
  // non-empty tuple so centroidFocusTarget always has at least one coordinate.
  const boothsForVendor = useCallback(
    (vendor: Vendor): [Vendor, ...Vendor[]] => {
      const filtered = vendors.filter((v) => v.uid === vendor.uid);
      return (filtered.length > 0 ? filtered : [vendor]) as [Vendor, ...Vendor[]];
    },
    [vendors],
  );

  /**
   * Derived ViewState (mobile). On desktop the map is always in view so
   * this is mostly informational; we still expose it for symmetry / tests.
   */
  const viewState: ViewState =
    showFullDetail && selectedVendor
      ? { kind: 'vendor-detail', vendor: selectedVendor }
      : { kind: 'tab', tab };

  // Map marker tap. On mobile, snapshot only (BottomSheet); on desktop go
  // straight into the full detail panel.
  const handleVendorTap = useCallback(
    (uid: string) => {
      vendorOpenSourceRef.current = 'map';
      openVendor(uid);
      if (isDesktop) {
        // No-op beyond setting selection: desktop panel auto-opens detail.
      } else {
        setShowFullDetail(false);
        setTab('map');
      }
    },
    [isDesktop, openVendor, setTab],
  );

  const handleTapVendorRow = useCallback(
    (vendor: Vendor) => {
      vendorOpenSourceRef.current = 'list';
      if (isDesktop) {
        openVendor(vendor.uid);
        // Navigating from the list to the map: fitBounds so all booths are
        // visible for multi-position vendors. Single-booth vendors fall back to
        // the normal single-point setView path inside centroidFocusTarget.
        requestAnimationFrame(() => setFocusTarget(centroidFocusTarget(boothsForVendor(vendor))));
        return;
      }
      openVendor(vendor.uid);
      // Remember the originating row so focus returns to it on close. The row
      // is about to unmount as the detail takes over the list tab.
      restoreRowFocusRef.current = vendor.uid;
      setShowFullDetail(true);
    },
    [isDesktop, openVendor, boothsForVendor],
  );

  const handleViewDetails = useCallback(
    (vendor: Vendor) => {
      openVendor(vendor.uid);
      if (!isDesktop) setShowFullDetail(true);
    },
    [isDesktop, openVendor],
  );

  const handleShowOnMap = useCallback(
    (vendor: Vendor) => {
      captureEvent('show_on_map_clicked', { vendor_uid: vendor.uid, vendor_name: vendor.name });
      openVendor(vendor.uid);
      if (isDesktop) {
        // Detail panel stays open on desktop — just pan the map.
      } else {
        setShowFullDetail(false);
        setTab('map');
      }
      // fitBounds so all booths are visible — panning to the centroid at the
      // current zoom would land in empty space between distant markers.
      requestAnimationFrame(() => setFocusTarget(centroidFocusTarget(boothsForVendor(vendor))));
    },
    [isDesktop, openVendor, setTab, boothsForVendor],
  );

  const handleTabChange = useCallback(
    (t: TabId) => {
      if (t !== tab) captureEvent('tab_changed', { tab: t, from: tab });
      setTab(t);
      setShowFullDetail(false);
    },
    [setTab, tab],
  );

  const handleBrowseListFromBanner = useCallback(() => {
    setShowVendorNotFound(false);
    handleTabChange('list');
  }, [handleTabChange]);

  // Indoor-season notice "Market info" link — router navigation to the
  // Info tab (goes through handleTabChange so tab_changed is captured).
  const handleIndoorNoticeInfo = useCallback(() => {
    handleTabChange('info');
  }, [handleTabChange]);

  const handleBackFromDetail = useCallback(() => {
    setShowFullDetail(false);
    if (isDesktop) closeVendor();
  }, [isDesktop, closeVendor]);

  const handleCloseSnapshot = useCallback(() => {
    closeVendor();
  }, [closeVendor]);

  // Selection-lost from ScaledMarkerLayer: either the current filter
  // dropped the active vendor, or a deep-linked uid resolves to nothing in
  // this week's dataset (`notFound`). In the dead-link case, latch the
  // not-found banner so the visitor gets feedback; either way clear the
  // URL so the stale selection doesn't re-open.
  const handleSelectionLost = useCallback(() => {
    if (vendorNotFound) setShowVendorNotFound(true);
    closeVendor();
  }, [vendorNotFound, closeVendor]);

  const handleMapTap = useCallback(() => {
    if (selectedVendor) closeVendor();
  }, [selectedVendor, closeVendor]);

  const handleSearchStart = useCallback(() => {
    if (!isEditing && tab !== 'list') setTab('list');
  }, [isEditing, tab, setTab]);

  if (loading) {
    return (
      <div className={styles.shell}>
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <img
              src="/assets/images/logo.png"
              alt=""
              width={72}
              height={72}
              className={styles.loadingLogo}
            />
            <h1 className={styles.loadingTitle}>Troy Waterfront Farmers Market</h1>
            <p className={styles.loadingSub}>Loading map…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && vendors.length === 0) {
    return (
      <div className={styles.shell}>
        <div className={styles.errorOverlay}>
          <div className={styles.errorContent}>
            <h2 className={styles.errorTitle}>Failed to load</h2>
            <p className={styles.errorMsg}>{error}</p>
            <button className={styles.retryBtn} onClick={refresh}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const errorBanner =
    error && errorKind ? (
      <ErrorBanner kind={errorKind} message={error} onRetry={refresh} onDismiss={dismissError} />
    ) : null;

  // Floating toast over the map for dead vendor deep links.
  const vendorNotFoundBanner = showVendorNotFound ? (
    <VendorNotFoundBanner
      onBrowseList={handleBrowseListFromBanner}
      onDismiss={dismissVendorNotFound}
    />
  ) : null;

  // SR announcement for the dead-link notice. The banner itself is not a
  // live region (it contains the browse link and dismiss button); this
  // persistent-empty region mirrors the revalidating one so screen readers
  // reliably announce the notice when it appears.
  const vendorNotFoundStatus = (
    <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
      {showVendorNotFound ? 'We couldn’t find that vendor — they may not be here this week.' : ''}
    </div>
  );

  // ----- Desktop -----
  if (isDesktop) {
    const segmentTab: 'vendors' | 'info' = tab === 'info' ? 'info' : 'vendors';
    const onInfo = segmentTab === 'info';
    const sidebarContent = onInfo ? (
      <InfoView />
    ) : (
      <ListView onTap={handleTapVendorRow} selectedVendorUid={vendorUid ?? undefined} />
    );

    // On desktop the right rail shows the full detail when a vendor is
    // selected (one-click). The snapshot is only used as a fallback when
    // we can't resolve the uid (rare).
    const rightPanel = selectedVendor ? (
      <VendorDetail
        vendor={selectedVendor}
        category={categoryById.get(selectedVendor.categoryId)}
        locationName={nameFor(selectedVendor.positionId)}
        variant="desktop"
        onBack={handleBackFromDetail}
        onShowOnMap={handleShowOnMap}
      />
    ) : null;

    return (
      <div className={styles.shell}>
        <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
          {revalidating ? 'Refreshing market data' : ''}
        </div>
        {vendorNotFoundStatus}
        <div className={`${styles.frame} ${styles.frameDesktop}`}>
          <header className={styles.desktopHeader}>
            <div className={styles.desktopHeaderMark}>
              <AppMark />
            </div>
            <SegmentedNav<DesktopSegmentId>
              segments={DESKTOP_SEGMENTS}
              active={segmentTab}
              onChange={(id) => handleTabChange(id === 'info' ? 'info' : 'list')}
              size="sm"
            />
            {!onInfo && (
              <div className={styles.desktopSearchWrap}>
                <SearchBar bare />
              </div>
            )}
            <div className={styles.desktopHeaderRight}>
              <HoursChip />
              <ThemeToggle />
            </div>
          </header>
          {errorBanner}
          <div className={styles.desktopChipsRow}>
            <FilterChips
              compact
              onChipInteract={onInfo ? () => handleTabChange('list') : undefined}
            />
          </div>
          <div
            className={[
              styles.desktopBody,
              rightPanel ? styles.desktopBodyWithRight : '',
              onInfo ? styles.desktopBodyInfo : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <aside className={styles.sidebar}>
              <div className={styles.sidebarContent}>{sidebarContent}</div>
            </aside>
            <main className={styles.mapArea}>
              <MapView
                selectedVendorUid={vendorUid ?? undefined}
                onVendorTap={handleVendorTap}
                focusTarget={focusTarget}
                onSelectionLost={handleSelectionLost}
                onMapTap={handleMapTap}
              />
              {vendorNotFoundBanner}
              <IndoorSeasonNotice onInfoClick={handleIndoorNoticeInfo} />
            </main>
            {rightPanel && (
              <aside className={styles.rightPanel}>
                <div className={styles.rightPanelInner}>{rightPanel}</div>
              </aside>
            )}
          </div>
        </div>
        {isEditing && (
          <Suspense fallback={null}>
            <LazyPositionEditor />
          </Suspense>
        )}
        {children}
      </div>
    );
  }

  // ----- Mobile -----
  // MapView stays mounted so panning/zoom state is preserved when you
  // open the detail view and come back. Other views render on top.
  const isDetail = viewState.kind === 'vendor-detail';
  const showSearchChrome =
    viewState.kind === 'tab' && (viewState.tab === 'map' || viewState.tab === 'list');
  const onMapTab = viewState.kind === 'tab' && viewState.tab === 'map';
  const onListTab = viewState.kind === 'tab' && viewState.tab === 'list';
  const onInfoTab = viewState.kind === 'tab' && viewState.tab === 'info';
  // BottomSheet declares aria-modal="true", so the background must actually
  // be inert while it's open: top stack, map layer, and bottom tabs all get
  // the `inert` attribute (removed automatically when the sheet closes).
  const sheetOpen = onMapTab && selectedVendor !== null;

  return (
    <div className={styles.shell}>
      <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
        {revalidating ? 'Refreshing market data' : ''}
      </div>
      {vendorNotFoundStatus}
      <div className={`${styles.frame} ${errorBanner ? styles.frameHasBanner : ''}`}>
        {errorBanner && <div inert={sheetOpen}>{errorBanner}</div>}
        {!isDetail && (
          <div className={styles.topStack} inert={sheetOpen}>
            <TopBar />
            {showSearchChrome && <SearchBar onSearchStart={handleSearchStart} />}
            {showSearchChrome && <FilterChips />}
          </div>
        )}

        <main ref={mainRef} role="main" className={styles.main}>
          {/* Map is always mounted so it keeps its view state. The sheet
              renders as a sibling (not a child) of the map layer so the
              whole layer can be inerted while the sheet is open. */}
          <div
            className={`${styles.mapLayer} ${onMapTab ? '' : styles.mapLayerHidden}`}
            aria-hidden={!onMapTab}
            inert={sheetOpen}
          >
            <MapView
              selectedVendorUid={vendorUid ?? undefined}
              onVendorTap={handleVendorTap}
              focusTarget={focusTarget}
              onSelectionLost={handleSelectionLost}
              onMapTap={handleMapTap}
              bottomInsetPx={sheetHeightPx}
            />
            {vendorNotFoundBanner}
            <IndoorSeasonNotice onInfoClick={handleIndoorNoticeInfo} />
          </div>
          {onMapTab && selectedVendor && (
            /* Backdrop: the map layer is inert while the sheet is open, so
               this wrapper catches taps outside the sheet and dismisses it —
               preserving the tap-map-to-close affordance under honest modal
               semantics. Keyboard users dismiss via Escape (useEscClose in
               BottomSheet), hence the a11y rule exemptions. */
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              className={styles.sheetWrap}
              data-testid="sheet-backdrop"
              onClick={(e) => {
                if (e.target === e.currentTarget) handleCloseSnapshot();
              }}
            >
              <BottomSheet
                onClose={handleCloseSnapshot}
                ariaLabel={`Vendor details: ${selectedVendor.name}`}
                onSheetMount={handleSheetMount}
                {...(selectedVendor.description
                  ? { ariaDescribedBy: VENDOR_SNAPSHOT_BLURB_ID }
                  : {})}
              >
                <VendorSnapshot
                  key={selectedVendor.uid}
                  vendor={selectedVendor}
                  category={categoryById.get(selectedVendor.categoryId)}
                  locationName={nameFor(selectedVendor.positionId)}
                  onClose={handleCloseSnapshot}
                  onViewDetails={handleViewDetails}
                />
              </BottomSheet>
            </div>
          )}

          {onListTab && (
            <div className={styles.overlay}>
              <ListView onTap={handleTapVendorRow} selectedVendorUid={vendorUid ?? undefined} />
            </div>
          )}
          {onInfoTab && (
            <div className={styles.overlay}>
              <InfoView />
            </div>
          )}
          {viewState.kind === 'vendor-detail' && (
            <div className={styles.overlay}>
              <VendorDetail
                vendor={viewState.vendor}
                category={categoryById.get(viewState.vendor.categoryId)}
                locationName={nameFor(viewState.vendor.positionId)}
                onBack={handleBackFromDetail}
                onShowOnMap={handleShowOnMap}
              />
            </div>
          )}
        </main>

        {!isDetail && <BottomTabs active={tab} onChange={handleTabChange} inert={sheetOpen} />}
      </div>

      {onMapTab && isEditing && (
        <Suspense fallback={null}>
          <LazyPositionEditor />
        </Suspense>
      )}
      {children}
    </div>
  );
}
